// src/pages/Wallet.js
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';

const METHODS = ['UPI (GPay/PhonePe)', 'Net Banking', 'NEFT/RTGS', 'Debit Card', 'Credit Card'];
const QUICK = [1000, 5000, 10000, 50000, 100000, 500000];

function StripeDepositForm({ amount, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/wallet`,
        },
        redirect: 'if_required',
      });

      if (result.error) {
        toast.error(result.error.message || 'Payment failed');
        return;
      }

      const status = result.paymentIntent?.status;
      if (status === 'succeeded') {
        await onSuccess(result.paymentIntent.id);
        return;
      }

      if (status === 'processing') {
        toast.success('Payment is processing. Balance will update shortly.');
        onCancel();
        return;
      }

      toast.error(`Payment status: ${status || 'unknown'}`);
    } catch (err) {
      toast.error(err?.message || 'Payment failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} style={{ marginTop: '1rem' }}>
      <div style={{ border: '1px solid var(--border)', padding: '1rem', background: 'var(--surface2)' }}>
        <PaymentElement />
      </div>
      <div style={{ display: 'flex', gap: '0.6rem', marginTop: '1rem' }}>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={!stripe || submitting}
          style={{ flex: 1, justifyContent: 'center' }}
        >
          {submitting ? 'PROCESSING...' : `PAY Rs.${Number(amount || 0).toLocaleString('en-IN')}`}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={submitting}
        >
          CANCEL
        </button>
      </div>
    </form>
  );
}

export default function Wallet() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('deposit');
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState(METHODS[0]);
  const [loading, setLoading] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [paymentsConfig, setPaymentsConfig] = useState({
    demo_enabled: true,
    stripe_enabled: false,
    stripe_publishable_key: '',
  });
  const [clientSecret, setClientSecret] = useState('');
  const [finalizingIntent, setFinalizingIntent] = useState(false);
  const redirectHandledRef = useRef(false);

  const stripePromise = useMemo(() => {
    if (!paymentsConfig.stripe_publishable_key) return null;
    return loadStripe(paymentsConfig.stripe_publishable_key);
  }, [paymentsConfig.stripe_publishable_key]);

  const loadWallet = async () => {
    try {
      const r = await api.get('/payments/wallet');
      setWallet(r.data);
    } catch {}
  };

  const loadPaymentsConfig = async () => {
    try {
      const r = await api.get('/payments/config');
      setPaymentsConfig({
        demo_enabled: Boolean(r.data?.demo_enabled),
        stripe_enabled: Boolean(r.data?.stripe_enabled),
        stripe_publishable_key: r.data?.stripe_publishable_key || '',
      });
    } catch {
      setPaymentsConfig({
        demo_enabled: true,
        stripe_enabled: false,
        stripe_publishable_key: '',
      });
    }
  };

  const finalizeStripeDeposit = async (paymentIntentId, fromRedirect = false) => {
    if (!paymentIntentId) return;
    setFinalizingIntent(true);
    try {
      const r = await api.post('/payments/deposit/stripe-confirm', {
        payment_intent_id: paymentIntentId,
      });
      await refreshUser();
      await loadWallet();
      setAmount('');
      setClientSecret('');
      toast.success(r.data?.message || 'Wallet credited successfully');
    } catch (err) {
      const message = err.response?.data?.message || 'Payment verification failed';
      if (fromRedirect) {
        toast.error(`Stripe return: ${message}`);
      } else {
        toast.error(message);
      }
    } finally {
      setFinalizingIntent(false);
    }
  };

  useEffect(() => {
    loadWallet();
    loadPaymentsConfig();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentIntentId = params.get('payment_intent');
    const redirectStatus = params.get('redirect_status');

    if (!paymentIntentId || redirectHandledRef.current) return;
    redirectHandledRef.current = true;

    window.history.replaceState({}, document.title, window.location.pathname);

    if (redirectStatus === 'succeeded') {
      finalizeStripeDeposit(paymentIntentId, true);
      return;
    }

    if (redirectStatus) {
      toast.error(`Stripe payment status: ${redirectStatus}`);
    }
  }, []);

  const redirectAddFunds = () => {
    navigate('/coming-soon?feature=wallet_topup');
  };

  const depositDemo = async () => {
    redirectAddFunds();
  };

  const startStripeDeposit = async () => {
    redirectAddFunds();
  };

  const withdraw = async () => {
    if (!amount || Number(amount) <= 0) return toast.error('Enter valid amount');
    if (Number(amount) > (user?.balance || 0)) return toast.error('Insufficient balance');
    setLoading(true);
    try {
      await api.post('/payments/withdraw', { amount: Number(amount) });
      await refreshUser();
      toast.success('Withdrawal initiated (T+1 to bank)');
      setAmount('');
      await loadWallet();
    } catch (err) { toast.error(err.response?.data?.message || 'Withdrawal failed'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto', padding: '3rem 2.5rem' }}>
      <div className="section-tag">Funds</div>
      <h1 className="section-title">MY <span style={{ color: 'var(--neon-green)' }}>WALLET</span></h1>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '1.5rem', marginBottom: '2rem' }}>
        {[
          { label: 'INR Balance', value: `Rs.${(user?.balance || 0).toLocaleString('en-IN')}`, color: 'var(--neon-green)', sub: 'Available for trading' },
          { label: 'Carbon Credits', value: `${user?.carbon_balance || 0} tCO2e`, color: 'var(--neon-cyan)', sub: 'Available to sell' },
        ].map(s => (
          <div key={s.label} className="card" style={{ borderTop: `2px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.6rem', letterSpacing: '3px', color: 'var(--muted)', textTransform: 'uppercase', marginBottom: '0.5rem' }}>{s.label}</div>
            <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '2.5rem', color: s.color, letterSpacing: '2px' }}>{s.value}</div>
            <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.65rem', color: 'var(--muted)', marginTop: '0.3rem' }}>{s.sub}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 0, marginBottom: '1.5rem', borderBottom: '1px solid var(--border)' }}>
        {[['deposit', 'Add Funds'], ['withdraw', 'Withdraw']].map(([k, l]) => (
          <button key={k} onClick={() => { setTab(k); setAmount(''); setClientSecret(''); }} style={{ padding: '0.8rem 2rem', background: 'transparent', border: 'none', borderBottom: `2px solid ${tab === k ? 'var(--neon-green)' : 'transparent'}`, color: tab === k ? 'var(--neon-green)' : 'var(--muted)', fontFamily: 'Space Mono, monospace', fontSize: '0.7rem', letterSpacing: '2px', textTransform: 'uppercase', cursor: 'pointer' }}>
            {l}
          </button>
        ))}
      </div>

      <div className="inline-actions" style={{ marginBottom: '1rem' }}>
        <button className="btn btn-secondary" type="button" onClick={() => navigate('/custody-wallet')}>Open custody wallet</button>
        <button className="btn btn-secondary" type="button" onClick={() => navigate('/markets')}>Open token markets</button>
      </div>

      <div className="card">
        {tab === 'deposit' && paymentsConfig.demo_enabled && (
          <div className="form-group">
            <label className="form-label">Payment Method</label>
            <select className="form-select" value={method} onChange={e => setMethod(e.target.value)}>
              {METHODS.map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
        )}
        <div className="form-group">
          <label className="form-label">Amount (Rs.)</label>
          <input className="form-input" type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="Enter amount" min="1" />
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
          {QUICK.map(q => (
            <button key={q} onClick={() => { setAmount(String(q)); setClientSecret(''); }} style={{ padding: '0.4rem 0.8rem', background: amount === String(q) ? 'rgba(0,255,136,0.15)' : 'var(--surface2)', border: `1px solid ${amount === String(q) ? 'var(--neon-green)' : 'var(--border)'}`, color: amount === String(q) ? 'var(--neon-green)' : 'var(--muted)', fontFamily: 'Space Mono, monospace', fontSize: '0.65rem', cursor: 'pointer', letterSpacing: '1px' }}>
              Rs.{q >= 100000 ? (q / 100000) + 'L' : q >= 1000 ? (q / 1000) + 'K' : q}
            </button>
          ))}
        </div>

        {tab === 'deposit' ? (
          <>
            {paymentsConfig.demo_enabled ? (
              <button className="btn btn-primary" onClick={depositDemo} disabled={loading} style={{ width: '100%', justifyContent: 'center', padding: '0.9rem' }}>
                {loading ? 'PROCESSING...' : 'ADD FUNDS'}
              </button>
            ) : (
              <>
                {!paymentsConfig.stripe_enabled ? (
                  <div style={{ border: '1px solid var(--neon-pink)', background: 'rgba(255,0,153,0.08)', color: 'var(--neon-pink)', padding: '0.8rem 1rem', fontFamily: 'Space Mono, monospace', fontSize: '0.65rem', lineHeight: '1.6' }}>
                    Stripe is not configured correctly. Set `STRIPE_SECRET_KEY` and `STRIPE_PUBLISHABLE_KEY` on backend.
                  </div>
                ) : !clientSecret ? (
                  <button className="btn btn-primary" onClick={startStripeDeposit} disabled={loading} style={{ width: '100%', justifyContent: 'center', padding: '0.9rem' }}>
                    {loading ? 'INITIALIZING...' : 'CONTINUE TO SECURE PAYMENT'}
                  </button>
                ) : stripePromise ? (
                  <Elements stripe={stripePromise} options={{ clientSecret }}>
                    <StripeDepositForm
                      amount={amount}
                      onSuccess={(paymentIntentId) => finalizeStripeDeposit(paymentIntentId, false)}
                      onCancel={() => setClientSecret('')}
                    />
                  </Elements>
                ) : (
                  <div style={{ color: 'var(--muted)', fontFamily: 'Space Mono, monospace', fontSize: '0.65rem' }}>
                    Unable to initialize Stripe.
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          <button className="btn btn-primary" onClick={withdraw} disabled={loading} style={{ width: '100%', justifyContent: 'center', padding: '0.9rem' }}>
            {loading ? 'PROCESSING...' : 'WITHDRAW'}
          </button>
        )}

        <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.6rem', color: 'var(--muted)', textAlign: 'center', marginTop: '1rem' }}>
          {tab === 'deposit'
            ? 'Wallet top-ups are coming soon. Thanks for your patience.'
            : 'T+1 processing via IMPS/NEFT.'}
        </div>
        {finalizingIntent && (
          <div style={{ marginTop: '0.8rem', color: 'var(--neon-cyan)', fontFamily: 'Space Mono, monospace', fontSize: '0.65rem', textAlign: 'center' }}>
            Verifying payment and updating wallet...
          </div>
        )}
      </div>

      {wallet?.transactions?.length > 0 && (
        <div className="card" style={{ marginTop: '1.5rem' }}>
          <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '1.2rem', letterSpacing: '2px', marginBottom: '1rem' }}>RECENT WALLET ACTIVITY</div>
          <table className="data-table">
            <thead><tr><th>Date</th><th>Type</th><th>Method</th><th>Amount</th><th>Reference</th><th>Status</th></tr></thead>
            <tbody>
              {wallet.transactions.slice(-8).reverse().map(t => (
                <tr key={t.id}>
                  <td style={{ color: 'var(--muted)' }}>{new Date(t.created_at).toLocaleDateString('en-IN')}</td>
                  <td>{t.type}</td>
                  <td>{t.payment_method || '-'}</td>
                  <td style={{ color: t.type === 'DEPOSIT' ? 'var(--neon-green)' : 'var(--neon-orange)' }}>
                    {t.type === 'DEPOSIT' ? '+' : '-'}Rs.{t.amount?.toLocaleString()}
                  </td>
                  <td style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.65rem', color: 'var(--muted)' }}>{t.reference}</td>
                  <td>
                    <span className={t.status === 'success' ? 'badge badge-green' : 'badge'} style={t.status !== 'success' ? { border: '1px solid var(--neon-yellow)', color: 'var(--neon-yellow)' } : {}}>
                      {(t.status || 'pending').toUpperCase()}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
